import { SkuMappingRow } from './SkuMappingRow';
import type { DemSkuMappingGroup, DemSkuMappingRow } from '../../types/dem';

type SkuMappingGroupProps = {
  group: DemSkuMappingGroup;
  expanded: boolean;
  onToggle: (pdfKey: string) => void;
  selectedRow: DemSkuMappingRow | null;
  onSelectRow: (row: DemSkuMappingRow) => void;
  onEditRow?: (row: DemSkuMappingRow) => void;
};

function isRowSelected(selected: DemSkuMappingRow | null, row: DemSkuMappingRow): boolean {
  return (
    selected?.pdfKey === row.pdfKey &&
    selected.sysCode === row.sysCode &&
    selected.factor === row.factor &&
    (selected.uom ?? '') === (row.uom ?? '')
  );
}

export function SkuMappingGroup({
  group,
  expanded,
  onToggle,
  selectedRow,
  onSelectRow,
  onEditRow,
}: SkuMappingGroupProps) {
  const multi = group.rows.length > 1;

  if (!multi) {
    return (
      <SkuMappingRow
        row={group.rows[0]}
        selected={isRowSelected(selectedRow, group.rows[0])}
        onSelect={onSelectRow}
        onEdit={onEditRow}
      />
    );
  }

  const selectedInGroup = group.rows.some((row) => isRowSelected(selectedRow, row));

  return (
    <div className={`dem-sku-map-group${expanded ? ' dem-sku-map-group--expanded' : ''}`}>
      <button
        type="button"
        className={`dem-sku-map-group__head${selectedInGroup && !expanded ? ' dem-sku-map-group__head--selected' : ''}`}
        onClick={() => onToggle(group.pdfKey)}
        aria-expanded={expanded}
      >
        <span className="dem-sku-map-group__lead">
          <span className="dem-sku-map-group__chevron" aria-hidden>
            {expanded ? '▾' : '▸'}
          </span>
          <span className="dem-sku-map-group__pdf" title={group.pdfKey}>
            {group.pdfKey}
          </span>
          {expanded ? <span className="dem-sku-map-group__collapse">Collapse</span> : null}
        </span>
        {!expanded ? (
          <span className="dem-sku-map-group__summary">{group.rows.length} system SKUs</span>
        ) : (
          <span className="dem-sku-map-group__ghost" aria-hidden />
        )}
        <span className="dem-sku-map-group__ghost" aria-hidden />
        <span className="dem-sku-map-group__ghost" aria-hidden />
        <span className="dem-sku-map-group__ghost" aria-hidden />
        <span className="dem-sku-map-group__ghost" aria-hidden />
      </button>

      {expanded
        ? group.rows.map((row) => (
            <SkuMappingRow
              key={`${row.pdfKey}|${row.sysCode}|${row.factor}|${row.uom ?? ''}|${row.lastUsed}`}
              row={row}
              variant="child"
              selected={isRowSelected(selectedRow, row)}
              onSelect={onSelectRow}
              onEdit={onEditRow}
            />
          ))
        : null}
    </div>
  );
}
